// src/components/FolioInput.jsx
import React, { useState, useEffect, useRef } from 'react';

/**
 * Componente de entrada para folios de boleto (4 dígitos numéricos)
 * con filtrado automático, debounce de 300ms y formato legible.
 *
 * @param {Object} props
 * @param {string} props.value - Valor actual del folio
 * @param {Function} props.onChange - Handler cuando cambia el texto
 * @param {Function} props.onDebounceFolio - Handler invocado 300ms después del último cambio
 * @param {boolean} props.isValidating - Si la API está validando actualmente
 * @param {boolean} props.disabled - Si el input está deshabilitado
 */
export default function FolioInput({
  value = '',
  onChange,
  onDebounceFolio,
  isValidating = false,
  disabled = false
}) {
  const [internalValue, setInternalValue] = useState(value);
  const debounceTimerRef = useRef(null);

  // Sincronizar si cambia desde props externamente (ej: query param t=0428)
  useEffect(() => {
    const sanitized = value.replace(/\D/g, '').slice(0, 4);
    setInternalValue(sanitized);
  }, [value]);

  // Manejo de cambios y temporizador de debounce (300ms)
  const handleChange = (e) => {
    // Solo permitir dígitos numéricos y máximo 4 caracteres
    const rawValue = e.target.value;
    const numericOnly = rawValue.replace(/\D/g, '').slice(0, 4);

    setInternalValue(numericOnly);
    if (onChange) {
      onChange(numericOnly);
    }

    // Limpiar timer previo de debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Configurar nuevo timer de 300ms
    debounceTimerRef.current = setTimeout(() => {
      if (onDebounceFolio) {
        onDebounceFolio(numericOnly);
      }
    }, 300);
  };

  // Limpiar timer al desmontar
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="folio-input-wrapper position-relative">
      <div className="input-group">
        <span className="input-group-text bg-dark border-secondary border-opacity-50 text-danger px-3">
          <i className="bi bi-ticket-perforated-fill fs-5"></i>
        </span>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={4}
          autoComplete="off"
          className="form-control form-control-dark font-monospace text-center fs-4 fw-bold letter-spacing-lg"
          placeholder="0000"
          value={internalValue}
          onChange={handleChange}
          disabled={disabled}
          aria-label="Folio del boleto a 4 dígitos"
        />
        {isValidating && (
          <span className="input-group-text bg-black border-secondary border-opacity-50 text-danger px-3">
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          </span>
        )}
      </div>
    </div>
  );
}
